import { firstValueFrom } from 'rxjs';

import { type Injector, runInInjectionContext } from '@angular/core';
import { ActivityEntityName, ActivityKind, ActivitySource, ActivityTagName, ActivityType } from '@jms/model';
import type { Activity, ActivityTag } from '@jms/model';
import { ContractorActivityService } from '@jms/portal/module.portal.contractor.operations/src/services';
import { WorkBuddyDialogService } from '@workbuddy/ui/services';
import type { ContractorBulkCommand, ContractorBulkOutcome } from './contractor.item.delete.bulk.action';
import { resolveContractorBulkLines, runContractorBulkLines, type ContractorBulkLine } from './contractor.item.bulk.tally';
import { buildJobItemBulkActionTitle } from './item.bulk.action.title';
import { ItemCommentBulkDialogComponent, type ItemCommentBulkDialogData } from './item.comment.dialog';

export const CONTRACTOR_ITEM_COMMENT_BULK_ACTION = 'items.contractor.comment';

/** One comment, posted as each line's own note through the portal note route, which finds the line's stage itself. */
export async function handleContractorItemCommentBulkCommand(args: ContractorBulkCommand, injector: Injector, itemsChanged?: () => void): Promise<ContractorBulkOutcome> {

  if (args?.action !== CONTRACTOR_ITEM_COMMENT_BULK_ACTION) return { handled: false };

  const lines = resolveContractorBulkLines(args.data);
  if (!lines.length) return { handled: true };

  const data: ItemCommentBulkDialogData = { title: buildJobItemBulkActionTitle('Add comment', args.data), count: lines.length };
  const ref = injector.get(WorkBuddyDialogService).open(ItemCommentBulkDialogComponent, { data });
  const body = ((await firstValueFrom(ref.onClose)) as string | undefined)?.trim();
  if (!body) return { handled: true };

  const activities = runInInjectionContext(injector, () => new ContractorActivityService());

  await runContractorBulkLines(lines, injector, async line => {
    await firstValueFrom(activities.save(buildContractorItemNote(body, line)));
    return { kind: 'updated' };
  }, itemsChanged, { noun: 'SOR(s)' });

  return { handled: true, clearSelection: true };
}

/** A SOR note as the portal composer writes it, minus the stage label a board row does not carry; its tags make the note show in the job and stage feeds too. */
function buildContractorItemNote(body: string, line: ContractorBulkLine): Activity {

  const tags: ActivityTag[] = [{ name: ActivityTagName.CodeId, value: line.itemId, ...(line.name ? { data: { _id: line.itemId, name: line.name, active: true } } : {}) }];
  if (line.jobId) tags.push({ name: ActivityTagName.JobId, value: line.jobId });
  if (line.stageId) tags.push({ name: ActivityTagName.WorkOrderId, value: line.stageId });

  return {
    _id: null,
    active: true,
    system: false,
    kind: ActivityKind.Note,
    type: ActivityType.EntityComment,
    entity: ActivityEntityName.Item,
    entityId: line.itemId,
    note: body,
    tags,
    source: ActivitySource.ContractorPortal,
  } as Activity;
}
